import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryMap: Record<string, string> = {
    'color-schemes': '配色方案',
    'design-tips': '设计技巧',
    'case-studies': '案例分析',
    'industry-trends': '行业趋势',
    'tools-resources': '工具资源',
  };

  const categoryName = categoryMap[article.category] || article.category;
  const relatedArticles = getRelatedArticles(slug!, article.category, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <div className="bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: categoryName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full font-medium hover:bg-primary-200 transition-colors"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-foreground-500">{article.readTime}</span>
                <span className="text-xs text-foreground-400">{article.date}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-600">作者：{article.author}</span>
              </div>
            </div>

            <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="article-body mb-12"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mb-12 pt-8 border-t border-background-200/70">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <div className="pt-8 border-t border-background-200/70">
                <h3 className="text-xl font-heading font-semibold text-foreground-900 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="group bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-full h-36 overflow-hidden">
                        <img
                          src={related.imageUrl}
                          alt={related.title}
                          title={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-snug">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{related.readTime}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.imageUrl,
            datePublished: article.date,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '色彩美学 ColorAesthetics',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://color-aesthetics.com/article/${slug}`,
            },
          }),
        }}
      />
    </>
  );
}